import {inject, Injectable} from '@angular/core';
import {ApiClient} from '../../common/infrastructure/network/ApiClient';
import {catchError, map, Observable, of} from 'rxjs';
import {ResultDto} from '../../common/dto/ResultDto';
import {CurrentUserProfileResponse} from '../dto/response/CurrentUserProfileResponse';
import {ResponseDto} from '../../common/dto/response/ResponseDto';
import {ErrorResponseDto} from '../../common/dto/response/ErrorResponseDto';
import {ErrorHandler} from '../../common/service/ErrorHandler';
import {ProfileMapper} from '../mappers/profile-mapper';
import {UserProfileDto} from '../dto/UserProfileDto';
import {UpdateCurrentUserProfileRequest} from '../dto/request/UpdateCurrentUserProfileRequest';

@Injectable({
  providedIn: 'root',
})
export class ProfileService extends ErrorHandler {

  private readonly currentProfileUrl = 'users/profile/current';

  private apiClient: ApiClient = inject(ApiClient);
  private mapper: ProfileMapper = inject(ProfileMapper);

  private currentProfile: UserProfileDto | undefined;

  public getCurrentProfile(): Observable<ResultDto<UserProfileDto | undefined>> {
    if (this.currentProfile) {
      return of(ResultDto.success(this.currentProfile));
    }
    return this.apiClient.get<ResponseDto<CurrentUserProfileResponse | ErrorResponseDto>>(this.currentProfileUrl)
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            const resp: CurrentUserProfileResponse = response.data as CurrentUserProfileResponse;
            const profile = this.mapper.fromCurrentProfileResponse(resp);
            this.currentProfile = profile;
            return ResultDto.success(profile);
          }
          return ResultDto.error('Profile fetch failed');
        }),
        catchError(err => this.handleError(err))
      );
  }

  public updateCurrentProfile(profile: UserProfileDto): Observable<ResultDto<UserProfileDto | undefined>> {
    const request = this.mapper.toUpdateCurrentUserProfileRequest(profile);
    return this.apiClient.put<UpdateCurrentUserProfileRequest, ResponseDto<CurrentUserProfileResponse | ErrorResponseDto>>(this.currentProfileUrl, request)
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            const resp: CurrentUserProfileResponse = response.data as CurrentUserProfileResponse;
            const profile = this.mapper.fromCurrentProfileResponse(resp);
            this.currentProfile = profile;
            return ResultDto.success(profile);
          }
          return ResultDto.error('Profile fetch failed');
        }),
        catchError(err => this.handleError(err))
      )
  }
}
